import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { apiDelete, apiGet, apiPatch, apiPost, errOf } from '../../lib/api';

type Kind = 'benefits' | 'news';

interface Item {
  id: string;
  title: string;
  body: string | null;
  partner?: string | null;
  valid_from?: string | null;
  valid_to?: string | null;
  published: number;
}
interface Form {
  title: string;
  body: string;
  partner: string;
  valid_from: string;
  valid_to: string;
  published: boolean;
}

const input: CSSProperties = {
  width: '100%',
  borderRadius: 8,
  padding: '6px 10px',
  border: '1px solid var(--color-divider)',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
  fontSize: 'var(--font-size-small)',
};
const th: CSSProperties = { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--color-divider)', fontSize: 'var(--font-size-tiny)' };
const td: CSSProperties = { padding: '6px 8px', borderBottom: '1px solid var(--color-divider)', fontSize: 'var(--font-size-small)', verticalAlign: 'top' };
const btn: CSSProperties = { padding: '4px 10px', borderRadius: 8, border: '1px solid var(--color-divider)', background: 'var(--color-card)', fontSize: 'var(--font-size-tiny)', cursor: 'pointer' };
const btnPrimary: CSSProperties = { ...btn, background: 'var(--color-primary)', color: 'var(--color-on-primary)' };
const blank: Form = { title: '', body: '', partner: '', valid_from: '', valid_to: '', published: false };

/** 內容管理（優惠／資訊；add／edit／delete）—— 只限職員。種類由路由 `:kind` 決定 */
export default function AdminContentPage() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const kind: Kind = pathname.endsWith('/news') ? 'news' : 'benefits';
  const [rows, setRows] = useState<Item[]>([]);
  const [form, setForm] = useState<Form | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    const r = await apiGet<{ items: Item[] }>(`/api/content/${kind}?all=1`);
    if (r.ok && r.data) setRows(r.data.items);
  }, [kind]);

  useEffect(() => {
    setForm(null);
    setEditId(null);
    void load();
  }, [load]);

  async function save() {
    if (!form) return;
    setErr('');
    const body = { ...form, sort_order: 0 };
    const r = editId
      ? await apiPatch(`/api/content/${kind}/${editId}`, body)
      : await apiPost(`/api/content/${kind}`, body);
    if (!r.ok) {
      setErr(errOf(r.data) || '儲存失敗');
      return;
    }
    setForm(null);
    setEditId(null);
    await load();
  }

  async function remove(id: string) {
    if (!window.confirm(t('confirm_delete'))) return;
    await apiDelete(`/api/content/${kind}/${id}`);
    await load();
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold">{t(kind === 'news' ? 'admin_news' : 'admin_benefits')}</h2>
        <button type="button" style={btnPrimary} onClick={() => { setEditId(null); setForm({ ...blank }); setErr(''); }}>
          ＋ {t('btn_new')}
        </button>
      </div>
      {form && (
        <div className="rounded-xl p-4 mb-4" style={{ background: 'var(--color-card)', boxShadow: 'var(--shadow-card)' }}>
          <label className="text-sm block">
            {t('f_title')}
            <input style={input} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </label>
          <label className="text-sm block mt-2">
            {t('f_body')}
            <textarea style={input} rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
          </label>
          {kind === 'benefits' && (
            <div className="grid gap-2 mt-2" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
              <label className="text-sm">
                {t('f_partner')}
                <input style={input} value={form.partner} onChange={(e) => setForm({ ...form, partner: e.target.value })} />
              </label>
              <label className="text-sm">
                {t('f_valid_from')}
                <input style={input} type="date" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} />
              </label>
              <label className="text-sm">
                {t('f_valid_to')}
                <input style={input} type="date" value={form.valid_to} onChange={(e) => setForm({ ...form, valid_to: e.target.value })} />
              </label>
            </div>
          )}
          <label className="flex items-center gap-2 mt-3 text-sm">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
            {t('f_published')}
          </label>
          {err && (
            <p className="text-sm mt-2" style={{ color: 'var(--color-danger)' }}>
              {err}
            </p>
          )}
          <div className="flex gap-2 mt-3">
            <button type="button" style={btnPrimary} onClick={() => void save()}>
              {t('btn_save')}
            </button>
            <button
              type="button"
              style={btn}
              onClick={() => {
                setForm(null);
                setEditId(null);
              }}
            >
              {t('btn_cancel')}
            </button>
          </div>
        </div>
      )}

      <div style={{ background: 'var(--color-card)', borderRadius: 12, overflow: 'hidden', boxShadow: 'var(--shadow-card)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>{t('col_title')}</th>
              {kind === 'benefits' && <th style={th}>{t('col_partner')}</th>}
              <th style={th}>{t('col_published')}</th>
              <th style={th} />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td style={td} colSpan={kind === 'benefits' ? 4 : 3}>
                  {t('empty_none')}
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id}>
                <td style={td}>{r.title}</td>
                {kind === 'benefits' && <td style={td}>{r.partner ?? '—'}</td>}
                <td style={td}>{r.published ? t('published_yes') : t('published_no')}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  <button
                    type="button"
                    style={btn}
                    onClick={() => {
                      setEditId(r.id);
                      setForm({
                        title: r.title,
                        body: r.body ?? '',
                        partner: r.partner ?? '',
                        valid_from: r.valid_from ?? '',
                        valid_to: r.valid_to ?? '',
                        published: Boolean(r.published),
                      });
                      setErr('');
                    }}
                  >
                    {t('btn_edit')}
                  </button>{' '}
                  <button type="button" style={btn} onClick={() => void remove(r.id)}>
                    {t('btn_delete')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
}
